import React from 'react';
import { useTranslation } from 'react-i18next';
import { BaseDocumentEditData } from '../../../../hooks/useDocumentEdit';

interface EditableDiscountSectionProps {
  data: BaseDocumentEditData;
  onUpdateType: (type: 'none' | 'percentage' | 'fixed') => void;
  onUpdateValue: (value: number) => void;
  accentColor: string;
}

/**
 * Section éditable pour la remise
 * Permet de choisir le type de remise et sa valeur
 */
export const EditableDiscountSection: React.FC<EditableDiscountSectionProps> = ({
  data,
  onUpdateType,
  onUpdateValue,
  accentColor: _accentColor,
}) => {
  const { t } = useTranslation();
  return (
    <div className="bg-white/40 backdrop-blur-sm border border-white/40 rounded-2xl p-4 space-y-4">
      <h3 className="text-sm font-semibold text-gray-700">{t('templates.common.discount')}</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Type de remise */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {t('templates.common.discountType')}
          </label>
          <select
            value={data.discountType || 'none'}
            onChange={(e) => {
              onUpdateType(e.target.value as 'none' | 'percentage' | 'fixed');
            }}
            className="w-full px-3 py-2.5 bg-white/40 backdrop-blur-sm border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white/60 transition-all duration-300"
          >
            <option value="none">{t('templates.common.noDiscount')}</option>
            <option value="percentage">{t('templates.common.percentage')}</option>
            <option value="fixed">{t('templates.common.fixedAmount')}</option>
          </select>
        </div>

        {/* Valeur de la remise */}
        {data.discountType !== 'none' && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('templates.common.discountValue')}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                step="0.01"
                value={data.discountValue || 0}
                onChange={(e) => onUpdateValue(parseFloat(e.target.value) || 0)}
                className="flex-1 px-3 py-2.5 bg-white/40 backdrop-blur-sm border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white/60 transition-all duration-300"
              />
              <span className="text-gray-600 font-medium text-sm">
                {data.discountType === 'percentage' ? '%' : '€'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Affichage de la remise calculée */}
      {data.discountAmount > 0 && (
        <div className="flex justify-between items-center px-4 py-3 bg-teal-50 border border-teal-200 rounded-xl">
          <span className="text-sm font-medium text-teal-700">
            {t('templates.common.discountApplied')} {data.discountType === 'percentage' ? `(${data.discountValue}%)` : ''}:
          </span>
          <span className="text-sm font-bold text-teal-700">
            - {data.discountAmount.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
          </span>
        </div>
      )}
    </div>
  );
};
